import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ImageOff, Film, ChevronDown, ChevronUp } from "lucide-react";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Field, AdminButton } from "@/pages/admin/ui";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "");
const STATUSES = ["Draft", "Paid", "Submitted", "Selected", "Not Selected"];
const selCls = "border border-ptf-primary/20 bg-white px-2 py-1.5 text-xs outline-none focus:border-ptf-secondary text-ptf-text";

function SettingsForm() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["last-horizon-status"], queryFn: async () => (await api.get("/last-horizon/status")).data });
  const [f, setF] = useState(null);
  const current = f || data || {};
  const set = (k) => (e) => setF({ ...current, [k]: e.target.value });
  const mut = useMutation({
    mutationFn: (body) => api.put("/admin/last-horizon/settings", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["last-horizon-status"] }); toast.success("Last Horizon settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = (e) => { e.preventDefault(); mut.mutate(current); };
  if (!data && !f) return <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />;
  return (
    <form onSubmit={save} className="space-y-4 max-w-xl">
      <Field label="Application Fee (INR)" type="number" value={current.application_fee ?? 5000} onChange={set("application_fee")} data-testid="lh-settings-fee" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Applications Open" type="date" value={current.application_open_date || ""} onChange={set("application_open_date")} data-testid="lh-settings-open-date" />
        <Field label="Applications Close" type="date" value={current.application_close_date || ""} onChange={set("application_close_date")} data-testid="lh-settings-close-date" />
      </div>
      <Field label="Selected Participants Announcement" type="date" value={current.announcement_date || ""} onChange={set("announcement_date")} data-testid="lh-settings-announce-date" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Kaziranga Start" type="date" value={current.kaziranga_start || ""} onChange={set("kaziranga_start")} data-testid="lh-settings-kaziranga-start" />
        <Field label="Kaziranga End" type="date" value={current.kaziranga_end || ""} onChange={set("kaziranga_end")} data-testid="lh-settings-kaziranga-end" />
        <Field label="Mumbai Transit" type="date" value={current.mumbai_transit_date || ""} onChange={set("mumbai_transit_date")} data-testid="lh-settings-mumbai-transit" />
        <Field label="Mumbai Departure" type="date" value={current.mumbai_departure_date || ""} onChange={set("mumbai_departure_date")} data-testid="lh-settings-mumbai-departure" />
        <Field label="Tanzania Start" type="date" value={current.tanzania_start || ""} onChange={set("tanzania_start")} data-testid="lh-settings-tanzania-start" />
        <Field label="Tanzania End" type="date" value={current.tanzania_end || ""} onChange={set("tanzania_end")} data-testid="lh-settings-tanzania-end" />
      </div>
      <Field label="Return Date" type="date" value={current.return_date || ""} onChange={set("return_date")} data-testid="lh-settings-return-date" />
      <AdminButton type="submit" data-testid="lh-settings-save">Save Settings</AdminButton>
    </form>
  );
}

function MediaThumb({ appId, kind, item }) {
  const [broken, setBroken] = useState(false);
  if (!item?.id) return null;
  const src = resolveImg(`/api/admin/last-horizon/media/${appId}/${kind}/${item.id}`);
  return (
    <div className="border border-ptf-primary/10 bg-white" data-testid={`lh-admin-media-${kind}-${item.id}`}>
      {broken ? (
        <div className="h-24 w-full grid place-items-center text-ptf-text/30"><ImageOff className="h-5 w-5" /></div>
      ) : kind === "video" ? (
        <video src={src} controls className="h-24 w-full bg-black" />
      ) : (
        <img src={src} alt="" onError={() => setBroken(true)} className="h-24 w-full object-cover" />
      )}
    </div>
  );
}

function ApplicationRow({ app }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const updateStatus = useMutation({
    mutationFn: (status) => api.put(`/admin/last-horizon/applications/${app.id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["last-horizon-admin-applications"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const isDrive = app.submission_mode === "drive_link";
  return (
    <div className="border border-ptf-primary/10 bg-white" data-testid={`lh-admin-app-${app.id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`lh-admin-app-toggle-${app.id}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-ptf-primary/5">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <div className="min-w-[160px]">
          <p className="font-medium text-ptf-primary text-sm">{app.name || "(unnamed)"}</p>
          <p className="text-[0.6rem] uppercase tracking-[0.1em] text-ptf-text/50">{app.email}</p>
        </div>
        <span className="text-xs text-ptf-text/60">{app.category || "-"}</span>
        <span className="text-xs text-ptf-text/60">{isDrive ? "Drive Link" : "Upload"}</span>
        <span className={`text-[0.6rem] uppercase tracking-[0.1em] px-2 py-1 ${app.payment_status === "paid" ? "bg-emerald-600/10 text-emerald-700" : "bg-ptf-primary/5 text-ptf-text/50"}`}>
          {app.payment_status === "paid" ? `Paid ₹${Number(app.amount || 0).toLocaleString("en-IN")}` : "Unpaid"}
        </span>
        <span className="text-xs text-ptf-text/45">{fmt(app.submitted_at || app.created_at)}</span>
        <select
          value={app.status} onChange={(e) => updateStatus.mutate(e.target.value)} disabled={updateStatus.isPending}
          data-testid={`lh-admin-app-status-${app.id}`} className={`${selCls} ml-auto`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-ptf-primary/10 pt-4 space-y-4" data-testid={`lh-admin-app-detail-${app.id}`}>
          <div className="grid sm:grid-cols-2 gap-3 text-xs text-ptf-text/70">
            <p><strong>Phone:</strong> {app.phone || "-"}</p>
            <p><strong>City / Country:</strong> {[app.city, app.country].filter(Boolean).join(", ") || "-"}</p>
            <p><strong>Camera / Device:</strong> {app.camera_device || "-"}</p>
            <p><strong>Public profile consent:</strong> {app.public_profile_consent ? "Yes" : "No"}</p>
            <p><strong>Instagram:</strong> {app.instagram || "-"}</p>
            <p><strong>LinkedIn:</strong> {app.linkedin || "-"}</p>
            <p><strong>Facebook:</strong> {app.facebook || "-"}</p>
            <p><strong>Order / Payment ID:</strong> {app.order_id || "-"} / {app.payment_id || "-"}</p>
          </div>
          <div className="bg-ptf-primary/[0.03] border border-ptf-primary/10 px-3 py-2" data-testid={`lh-admin-terms-record-${app.id}`}>
            <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-primary/60 mb-1">Signed Acceptance Record</p>
            {app.terms_accepted_at ? (
              <p className="text-xs text-ptf-text/70">
                Terms &amp; Conditions <strong>v{app.terms_version || "1"}</strong> accepted on {fmt(app.terms_accepted_at)}
                {app.terms_accept_ip ? ` from IP ${app.terms_accept_ip}` : ""}
              </p>
            ) : (
              <p className="text-xs text-ptf-text/45">Not yet accepted.</p>
            )}
          </div>
          {isDrive ? (
            <div className="text-xs text-ptf-text/70 space-y-1.5">
              <p className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-primary/50">Google Drive Links</p>
              {(app.photo_links || []).map((l, i) => l.url && <p key={l.url || i}><a href={l.url} target="_blank" rel="noopener noreferrer" className="underline text-ptf-secondary">Photo {i + 1}</a>{l.caption ? ` (${l.caption})` : ""}</p>)}
              {app.video_link?.url && <p><a href={app.video_link.url} target="_blank" rel="noopener noreferrer" className="underline text-ptf-secondary inline-flex items-center gap-1"><Film className="h-3.5 w-3.5" /> Video Link</a></p>}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {(app.photos || []).map((p) => <MediaThumb key={p.id} appId={app.id} kind="photo" item={p} />)}
                {app.video?.id && <MediaThumb appId={app.id} kind="video" item={app.video} />}
              </div>
              {app.drive_folder_url && (
                <a href={app.drive_folder_url} target="_blank" rel="noopener noreferrer" data-testid={`lh-admin-drive-link-${app.id}`} className="inline-flex items-center gap-1.5 text-xs underline text-ptf-secondary hover:text-ptf-primary">
                  View backup in Google Drive
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrainingDiscountForm() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["training-discount-settings"], queryFn: async () => (await api.get("/admin/training-discount-settings")).data });
  const [f, setF] = useState(null);
  const current = f || data || {};
  const mut = useMutation({
    mutationFn: (body) => api.put("/admin/training-discount-settings", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["training-discount-settings"] }); toast.success("Training discount settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = (e) => {
    e.preventDefault();
    mut.mutate({
      percent: Number(current.percent ?? 25),
      active: current.active !== false,
    });
  };
  if (!data && !f) return <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />;
  return (
    <form onSubmit={save} className="space-y-4 max-w-xl border border-ptf-primary/12 p-5">
      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">Member Training Discount</p>
      <p className="text-xs text-ptf-text/60 leading-relaxed">
        Members with an active Foundation membership (incl. those granted one via a paid Last Horizon application) get this discount,
        only on programmes marked "Eligible for Membership Discount" in that programme's edit form. Never applies to The Last Horizon's
        own application fee.
      </p>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={current.active !== false} onChange={(e) => setF({ ...current, active: e.target.checked })} data-testid="training-discount-active" className="h-4 w-4 accent-ptf-secondary" />
        <span className="text-sm font-medium text-ptf-primary">Discount active (site-wide switch)</span>
      </label>
      <Field label="Discount Percent" type="number" min="0" max="100" value={current.percent ?? 25} onChange={(e) => setF({ ...current, percent: e.target.value })} data-testid="training-discount-percent" />
      <AdminButton type="submit" data-testid="training-discount-save">Save Discount Settings</AdminButton>
    </form>
  );
}

export default function LastHorizonAdmin() {
  const { data: apps = [], isLoading } = useQuery({ queryKey: ["last-horizon-admin-applications"], queryFn: async () => (await api.get("/admin/last-horizon/applications")).data });
  const [filter, setFilter] = useState("");
  const filtered = filter ? apps.filter((a) => a.status === filter) : apps;
  const paidCount = apps.filter((a) => a.payment_status === "paid").length;
  const selectedCount = apps.filter((a) => a.status === "Selected").length;

  const exportCsv = () => {
    downloadCsv(
      [["Name", "Email", "Phone", "Category", "Mode", "Status", "Payment Status", "Amount", "Submitted At"],
        ...filtered.map((a) => [a.name, a.email, a.phone, a.category, a.submission_mode, a.status, a.payment_status, a.amount, a.submitted_at])],
      "last-horizon-applications.csv"
    );
  };

  return (
    <div className="space-y-12">
      <div>
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Challenge Settings</h2>
        <SettingsForm />
      </div>
      <div>
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Training Discount</h2>
        <TrainingDiscountForm />
      </div>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">
            Applications ({apps.length}) · {paidCount} Paid · {selectedCount}/6 Selected
          </h2>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="lh-admin-filter-status" className={selCls}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <AdminButton type="button" variant="secondary" onClick={exportCsv} data-testid="lh-admin-export-csv">Export CSV</AdminButton>
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />
        ) : filtered.length === 0 ? (
          <p className="text-sm text-ptf-text/50 italic font-serif-ed">No applications yet.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((a) => <ApplicationRow key={a.id} app={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}
